import React, { memo, useState, useEffect, useCallback } from 'react';

import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import { useSelector } from '../../redux/store';
import useLocales from '../../hooks/useLocales';
import { getDocument } from '../../redux/slices/document';
import DocumentHistory from '../documents/DocumentHistory';
import useNotifications from '../../hooks/useNotifications';
import documentService from '../../services/documentService';
import { ModuleTypes } from '../../@types/module/enums/moduleTypes';
import ModuleCheck from '../../components/access-checks/ModuleCheck';
import FilePreviewSections, { TempFile } from './FilePreviewSections';
import type { FormContent, Collaborator } from '../../@types/forms/types';
import { DocumentModuleTypes } from '../../@types/shared/enums/documentModuleTypes';
import DocumentAccompanyingDocuments from '../documents/DocumentAccompanyingDocuments';
import type { FieldErrors, CollaboratorFieldsGroup } from './utils/formValidationUtils';
import CollaboratorsWithFieldsTable from './collaborators/CollaboratorsWithFieldsTable';

// ----------------------------------------------------------------------

interface FormSidebarEditDocumentProps {
  // Collaborators
  collaborators: Collaborator[];
  setCollaborators: React.Dispatch<React.SetStateAction<Collaborator[]>>;
  collaboratorsErrors: string[];
  setCollaboratorsErrors: React.Dispatch<React.SetStateAction<string[]>>;

  // Fields
  fieldsGroupedByCollaborator: CollaboratorFieldsGroup[];
  fieldErrors: FieldErrors;
  onFieldClick: (fieldId: string) => void;

  // File uploads
  documentId: string;
  formModuleType: DocumentModuleTypes | undefined;
  onCombinedModeClick: () => void;

  // Content
  setContent: React.Dispatch<React.SetStateAction<FormContent[]>>;

  // Temp files (lifted to Form.tsx for combined mode)
  setMainTempFiles: React.Dispatch<React.SetStateAction<TempFile[]>>;
}

/**
 * Sidebar component containing all right-side sections
 * Memoized to prevent unnecessary re-renders when parent re-renders
 */
const FormSidebarEditDocument: React.FC<FormSidebarEditDocumentProps> = ({
  collaborators,
  setCollaborators,
  collaboratorsErrors,
  setCollaboratorsErrors,
  fieldsGroupedByCollaborator,
  fieldErrors,
  onFieldClick,
  documentId,
  formModuleType,
  onCombinedModeClick,
  setContent,
  setMainTempFiles,
}) => {
  const { t } = useLocales();
  const { document } = useSelector((state) => state.document);
  const { enqueueError } = useNotifications();
  const [mainTempFilesLocal, setMainTempFilesLocal] = useState<TempFile[]>([]);
  const [attachmentsTempFiles, setAttachmentsTempFiles] = useState<TempFile[]>([]);

  // Handle document loading
  const handleGetDocument = useCallback(
    async (id: number) => {
      try {
        await getDocument(id);

        const files = await documentService.getTempFiles(id);
        const { attachments, mainFile } = files;

        if (attachments) {
          setAttachmentsTempFiles(attachments);
        }

        if (mainFile) {
          setMainTempFilesLocal(mainFile);
          setMainTempFiles(mainFile);
        }
      } catch (error) {
        enqueueError(error);
      }
    },
    [enqueueError, setMainTempFiles]
  );

  useEffect(() => {
    if (documentId) {
      handleGetDocument(Number(documentId));
    }
  }, [documentId, handleGetDocument]);

  useEffect(() => {
    if (document?.fbFilledForm) {
      setCollaborators(document.fbFilledForm.collaborators);

      const parsedContent: FormContent[] = JSON.parse(document.fbFilledForm.content);
      setContent(parsedContent);
    }
  }, [document, setCollaborators, setContent]);

  return (
    <Grid container spacing={2}>
      {/* Collaborators with Fields Section */}
      <ModuleCheck module={ModuleTypes.CollabForms}>
        {collaborators.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Accordion defaultExpanded sx={{ p: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
                <Typography variant="h6">{t('forms.collaborators')}</Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 3, pt: 0 }}>
                <CollaboratorsWithFieldsTable
                  collaborators={collaborators}
                  setCollaborators={setCollaborators}
                  collaboratorsErrors={collaboratorsErrors}
                  setCollaboratorsErrors={setCollaboratorsErrors}
                  fieldsGroupedByCollaborator={fieldsGroupedByCollaborator}
                  fieldErrors={fieldErrors}
                  onFieldClick={onFieldClick}
                  isEditMode
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </ModuleCheck>

      {/* File Preview Sections */}
      {((formModuleType === DocumentModuleTypes.Accounting && mainTempFilesLocal.length > 0) ||
        attachmentsTempFiles.length > 0) && (
        <FilePreviewSections
          mainTempFiles={mainTempFilesLocal}
          attachmentsTempFiles={attachmentsTempFiles}
          formModuleType={formModuleType || DocumentModuleTypes.Standard}
          onCombinedModeClick={mainTempFilesLocal.length > 0 ? onCombinedModeClick : undefined}
        />
      )}

      <Grid size={{ xs: 12 }}>
        <DocumentHistory />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <DocumentAccompanyingDocuments />
      </Grid>
    </Grid>
  );
};

export default memo(FormSidebarEditDocument);
